/**
 * The section creates currently in flight — for readers that have to measure
 * the AHU's geometry back afterwards.
 *
 * Why it exists: inserting a section is a CHAIN of writes (tunnel POST →
 * section POST → component POST), and on a hybrid AHU the tunnel the first of
 * those creates only acquires a footprint when the SECTION lands — a tunnel's
 * `length` is the sum of its sections. So between the two POSTs the AHU really
 * does hold a tunnel at the end of the chain that measures zero and does not
 * move the chain's end.
 *
 * The hybrid Add Section dialog measures exactly that: every row's X-Offset
 * default butts the new tunnel against the chain's current end
 * (`defaultXOffset`). Reopened in that window — one insert straight after
 * another, which is how a hybrid unit gets built — it offered the offset it
 * had just used, so the new section landed exactly on top of the previous one
 * and (`_auto_address_hybrid_peers` breaking an exact tie newest-first) was
 * numbered AHEAD of the section it was added after. Reported 2026-10-08:
 * "the second section must be positioned after the first".
 *
 * The window opens at the GESTURE, not at the section POST: the tunnel POST in
 * front of it takes a second or more on a loaded box, so a marker that starts
 * only when the section mutation does is not yet pending when the dialog
 * reopens — measured, and the default repeated exactly as before. It closes as
 * soon as the sections are committed rather than at the end of the whole
 * chain, because the sections are the only part of it the geometry depends on:
 * waiting for the component POST and the lug/placement re-reads as well left
 * the dialog blank for up to ~3s on a loaded box for no gain.
 */
let pending: Promise<unknown> = Promise.resolve();

/**
 * Register an in-flight section create. Chained rather than replaced: two can
 * overlap (a dual-tunnel insert writes two, a ctrl-click run more), and a
 * waiter wants all of them committed, not just the most recent.
 */
export const trackSectionWrite = (write: Promise<unknown>): void => {
  pending = Promise.allSettled([pending, write]);
};

/**
 * Open the window by hand, for a gesture whose section writes have not been
 * issued yet. Returns the function that closes it — call it as soon as the
 * sections have landed, and again (harmlessly) from a `finally` so a failed
 * gesture can never leave a waiter hanging.
 */
export const beginSectionWrites = (): (() => void) => {
  let close: () => void = () => undefined;
  trackSectionWrite(
    new Promise<void>((resolve) => {
      close = resolve;
    })
  );
  return () => close();
};

/**
 * Resolves once no section create is outstanding. Never rejects — a failed
 * write is still a settled one, and the caller re-reads from the server
 * anyway.
 */
export const awaitSectionWrites = (): Promise<unknown> => pending;
